import { FC, useContext, useEffect, useRef, useState, Fragment } from "react";
import { TranslationContext } from "../../context/TranslationContext";
import { Dialog, Transition } from "@headlessui/react";

import LocationPickerHelper from "../locationPickerHelper";
import classNames from "../../utils/twClassNames";

type Props = {
  location: string;
  distance: number;
  setDistance: (distance: number) => void;
  withHelper?: boolean;
  withPopup?: boolean;
  maxDistance?: number;
  distanceUnit?: string;
  shouldShowSlider?: boolean;
};

const DistanceSelectSlider: FC<Props> = ({
  location,
  distance,
  setDistance,
  withHelper = false,
  withPopup = false,
  maxDistance = 550,
  distanceUnit = "km",
  shouldShowSlider = true,
}) => {
  const { i } = useContext(TranslationContext);

  const minDistance = 5;

  const progressBarRef = useRef<HTMLDivElement>(null);

  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [step, setStep] = useState(5);

  const maxDistanceWithExtraStepForModulo = maxDistance + 25;

  useEffect(() => {
    if (progressBarRef.current) {
      progressBarRef.current.style.left = "0";

      const distanceFromRight =
        100 -
        ((distance - minDistance) /
          (maxDistanceWithExtraStepForModulo - minDistance)) *
          100 +
        "%";

      progressBarRef.current.style.right = distanceFromRight;
    }

    setStep((prevState) => {
      if (prevState === 25 && distance === 50) {
        return 5;
      }

      return distance < 50 ? 5 : 25;
    });
  }, [distance]);

  const handleDistance = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!shouldShowSlider) return;

    const newDistance = parseInt(e.target.value);

    if (isValidNewDistance(newDistance)) {
      setDistance(
        newDistance > 50 ? newDistance - (newDistance % 25) : newDistance
      );
    }
  };

  function isValidNewDistance(newDistance: number) {
    return (
      newDistance <= maxDistanceWithExtraStepForModulo &&
      newDistance >= minDistance
    );
  }

  return (
    <>
      <div className="DistanceSelectSlider relative">
        <div className="block md:flex">
          {withHelper && <LocationPickerHelper location={location} />}
          {!withHelper && (
            <div className="block mb-1.5 text-base font-semibold text-custom-black">
              {location}
            </div>
          )}
          {!withHelper && withPopup && (
            <svg
              onClick={() => setRegisterModalOpen(true)}
              height={15}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 512"
              className="mt-1 ml-1"
            >
              <path
                fill="#ec623b"
                d="M320 144C320 223.5 255.5 288 176 288C96.47 288 32 223.5 32 144C32 64.47 96.47 0 176 0C255.5 0 320 64.47 320 144zM192 64C192 55.16 184.8 48 176 48C122.1 48 80 90.98 80 144C80 152.8 87.16 160 96 160C104.8 160 112 152.8 112 144C112 108.7 140.7 80 176 80C184.8 80 192 72.84 192 64zM144 480V317.1C154.4 319 165.1 319.1 176 319.1C186.9 319.1 197.6 319 208 317.1V480C208 497.7 193.7 512 176 512C158.3 512 144 497.7 144 480z"
              />
            </svg>
          )}
        </div>

        <div
          style={
            shouldShowSlider
              ? {}
              : {
                  opacity: 0.3,
                  filter: "blur(2px)",
                }
          }
          className="flex flex-row items-center h-[45px] space-x-1.5"
        >
          <div className="w-full slider relative h-1.5 bg-custom-gray rounded-md">
            <div
              className="absolute h-1.5 bg-custom-orange rounded-md"
              ref={progressBarRef}
            ></div>
            <div className="relative">
              <input
                data-testid="distance-select-slider-input"
                onChange={handleDistance}
                step={step}
                type="range"
                min={minDistance}
                max={maxDistanceWithExtraStepForModulo}
                value={distance}
                className="absolute w-full h-1.5 bg-transparent appearance-none pointer-events-none"
              />
            </div>
          </div>
          <div
            data-testid="distance-select-slider-distance"
            className="flex items-center justify-center min-w-[75px] h-full border-custom-gray border rounded-md bg-white text-custom-black"
          >
            {distance > 525
              ? i("All")
              : distance > 500
              ? i("Country")
              : distance + distanceUnit}
          </div>
        </div>
        {!shouldShowSlider && (
          <p className="absolute -bottom-3 text-custom-black"></p>
        )}
      </div>
      <Transition appear show={registerModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setRegisterModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-medium leading-6 text-gray-900 mb-6"
                  >
                    {i("Want more options? It's 100% free!")}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-gray-500">
                      {i(
                        "Our advanced search is a privilege reserved for our members. If you are already one, log in and if you are not yet a member, register. Love may be right here at your fingertips."
                      )}
                    </p>
                  </div>

                  <div className="mt-6 flex">
                    <div>
                      <a
                        href="/inscription.php"
                        className="btn-primary w-[120px] h-12 "
                      >
                        {i("Register")}
                      </a>
                    </div>

                    <div>
                      <a
                        href={i("https://www.vidamora.com/sign-in")}
                        className="btn-white w-[120px] h-12 ml-3"
                      >
                        {i("Login")}
                      </a>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DistanceSelectSlider;
